<template>
  <v-container
    id="user-profile"
    fluid
    tag="section"
    class="up-page"
  >
    <!-- Título da Página -->
    <v-row justify="center">
      <v-col cols="12" md="10" class="pb-0">
        <div class="up-page-title">Perfil do Usuário</div>
        <div class="up-page-subtitle">Gerencie suas informações pessoais e de acesso.</div>
      </v-col>
    </v-row>

    <v-row justify="center">
      <!-- Card de Foto de Perfil -->
      <v-col
        cols="12"
        md="3"
      >
        <v-card class="up-profile-card" elevation="0">
          <!-- Banner -->
          <div class="up-card-banner"></div>

          <div class="up-profile-body px-5 pb-5">
            <!-- Avatar -->
            <div class="avatar-upload-container">
              <v-avatar
                :color="user.avatarUrl ? 'transparent' : getAvatarColor(user.name)"
                size="104"
                class="avatar-clickable"
                @click="!uploading && !isViewOnlyMode && triggerFileInput()"
              >
                <v-progress-circular
                  v-if="uploading"
                  :size="60"
                  :width="6"
                  color="primary"
                  indeterminate
                />
                <v-img
                  v-else-if="user.avatarUrl || previewAvatar"
                  :src="previewAvatar || user.avatarUrl"
                  alt="Avatar"
                />
                <span
                  v-else
                  class="white--text text-h3"
                >
                  {{ getInitials(user.name) }}
                </span>
              </v-avatar>

              <v-btn
                v-if="!isViewOnlyMode"
                fab
                x-small
                class="avatar-upload-btn up-cyan-btn"
                :loading="uploading"
                :disabled="uploading"
                @click="triggerFileInput"
              >
                <v-icon small>mdi-camera</v-icon>
              </v-btn>

              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onFileSelected"
              >
            </div>

            <!-- User Info -->
            <div class="up-user-name mt-3">
              {{ user.name }}
            </div>
            <div class="up-user-role">
              {{ user.jobRoleName || 'Colaborador' }}
            </div>
            <div v-if="user.departmentName && user.departmentName !== '-'" class="up-user-dept">
              {{ user.departmentName }}
            </div>

            <v-divider class="up-divider my-4" />

            <!-- Contact Info -->
            <v-list dense class="transparent pa-0">
              <v-list-item
                v-if="user.email"
                dense
                class="px-0 up-list-item"
              >
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon small color="#22d3ee">mdi-email-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-left up-contact-text">
                    {{ user.email }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-if="user.phone"
                dense
                class="px-0 up-list-item"
              >
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon small color="#22d3ee">mdi-phone-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-left up-contact-text">
                    {{ user.phone }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-if="user.hireDate"
                dense
                class="px-0 up-list-item"
              >
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon small color="#22d3ee">mdi-calendar-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-left up-contact-text">
                    Desde {{ formatDate(user.hireDate) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-if="user.tenantName"
                dense
                class="px-0 up-list-item"
              >
                <v-list-item-icon class="mr-3 my-auto">
                  <v-avatar v-if="user.tenantAvatar" size="18">
                    <v-img :src="user.tenantAvatar" />
                  </v-avatar>
                  <v-icon v-else small color="#22d3ee">mdi-domain</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-left up-contact-text font-weight-medium">
                    {{ user.tenantName }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-divider class="up-divider mt-3 mb-4" />

            <v-btn
              v-if="!isViewOnlyMode"
              block
              outlined
              small
              color="#22d3ee"
              class="text-none up-reset-btn"
              @click="openPasswordDialog"
            >
              <v-icon left small>mdi-lock-reset</v-icon>
              Redefinir Senha
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <!-- Card de Edição de Perfil -->
      <v-col
        cols="12"
        md="7"
      >
        <v-card class="up-edit-card" elevation="0">
          <!-- Header -->
          <div class="up-edit-header px-5 pt-5 pb-4">
            <div class="up-edit-title">Editar Perfil</div>
            <div class="up-edit-subtitle">Atualize suas informações pessoais</div>
          </div>
          <v-divider class="up-divider" />

          <v-form
            ref="form"
            v-model="valid"
            @submit.prevent="!isViewOnlyMode && updateProfile()"
          >
            <v-container class="pa-5">
              <v-row>
                <!-- Nome Completo -->
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.name"
                    label="Nome Completo *"
                    class="purple-input"
                    :rules="[rules.required]"
                    outlined
                    dense
                    :readonly="isViewOnlyMode"
                  />
                </v-col>

                <!-- Email -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="formData.email"
                    label="Email Corporativo *"
                    class="purple-input"
                    :rules="[rules.required, rules.email]"
                    outlined
                    dense
                    type="email"
                    readonly
                    disabled
                    hint="O e-mail corporativo não pode ser alterado"
                  />
                </v-col>

                <!-- Telefone/WhatsApp -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="formData.phone"
                    label="Telefone/WhatsApp"
                    class="purple-input"
                    outlined
                    dense
                    placeholder="+55 11 99999-9999"
                    hint="Formato: +55 11 99999-9999"
                    persistent-hint
                    :readonly="isViewOnlyMode"
                  />
                </v-col>

                <!-- Data de Nascimento -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="formData.birthDate"
                    label="Data de Nascimento"
                    class="purple-input"
                    outlined
                    dense
                    type="date"
                    hint="Selecione sua data de nascimento"
                    :readonly="isViewOnlyMode"
                  />
                </v-col>

                <!-- Data de Contratação (Somente leitura) -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="formData.hireDate"
                    label="Data de Contratação"
                    class="purple-input"
                    outlined
                    dense
                    type="date"
                    readonly
                    hint="Este campo não pode ser alterado"
                    persistent-hint
                  />
                </v-col>

                <!-- Cargo (Somente leitura) -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="user.jobRoleName"
                    label="Cargo"
                    outlined
                    dense
                    readonly
                    disabled
                    hint="Gerenciado pelo RH"
                  />
                </v-col>

                <!-- Departamento (Somente leitura) -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="user.departmentName"
                    label="Departamento"
                    outlined
                    dense
                    readonly
                    disabled
                    hint="Gerenciado pelo RH"
                  />
                </v-col>

                <!-- Gestor (Somente leitura) -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="user.managerName"
                    label="Gestor Direto"
                    outlined
                    dense
                    readonly
                    disabled
                    hint="Gerenciado pelo RH"
                  />
                </v-col>

                <!-- Perfil de Acesso (Somente leitura) -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    :value="getRoleLabel(user.role)"
                    label="Perfil de Acesso"
                    outlined
                    dense
                    readonly
                    disabled
                    hint="Define suas permissões no sistema"
                    persistent-hint
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="#22d3ee">mdi-shield-account</v-icon>
                    </template>
                  </v-text-field>
                </v-col>

                <!-- Status (Somente leitura) -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    :value="getStatusLabel(user.status)"
                    label="Status da Conta"
                    outlined
                    dense
                    readonly
                    disabled
                  />
                </v-col>

                <!-- Botões de Ação -->
                <v-col
                  cols="12"
                  class="text-right"
                >
                  <v-btn
                    v-if="!isViewOnlyMode"
                    text
                    class="mr-2"
                    @click="resetForm"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    v-if="!isViewOnlyMode"
                    color="#FF771C"
                    type="submit"
                    :loading="saving"
                    :disabled="!valid || !hasChanges"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Salvar Alterações
                  </v-btn>
                  <v-alert
                    v-if="isViewOnlyMode"
                    type="info"
                    text
                    dense
                    class="mb-0 mt-2"
                    icon="mdi-eye"
                  >
                    Modo visualização — alterações não são permitidas
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog de Crop de Imagem -->
    <v-dialog
      v-model="cropDialog"
      max-width="600"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">
          <v-icon left color="primary">mdi-crop</v-icon>
          Ajustar Imagem de Perfil
        </v-card-title>

        <v-card-text>
          <div class="crop-container">
            <div ref="cropperContainer" style="max-height: 400px;">
              <img
                ref="cropperImage"
                :src="imageToCrop"
                style="max-width: 100%"
              >
            </div>
          </div>

          <v-alert
            text
            color="info"
            icon="mdi-information"
            class="mt-4"
          >
            <small>
              Ajuste a área de corte para 1:1 (quadrado). A imagem será redimensionada automaticamente para 300x300px.
            </small>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="cancelCrop"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="uploading"
            :disabled="isViewOnlyMode"
            @click="confirmCrop"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar e Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Redefinição de Senha -->
    <v-dialog
      v-model="passwordDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          <v-icon left color="white">mdi-lock-reset</v-icon>
          Redefinir Senha
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert
            text
            color="info"
            icon="mdi-shield-check"
            class="mb-4"
          >
            <small>
              Por segurança, você precisa confirmar sua senha atual antes de definir uma nova.
            </small>
          </v-alert>

          <v-form
            ref="passwordForm"
            v-model="passwordFormValid"
          >
            <!-- Senha Atual -->
            <v-text-field
              v-model="passwordForm.currentPassword"
              label="Senha Atual *"
              outlined
              dense
              :type="showCurrentPassword ? 'text' : 'password'"
              :rules="[rules.required]"
              prepend-inner-icon="mdi-lock-outline"
              :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showCurrentPassword = !showCurrentPassword"
              :error-messages="passwordError"
              class="mb-2"
            />

            <v-divider class="my-4" />

            <!-- Nova Senha -->
            <v-text-field
              v-model="passwordForm.newPassword"
              label="Nova Senha *"
              outlined
              dense
              :type="showNewPassword ? 'text' : 'password'"
              :rules="[rules.required, validateNewPassword]"
              prepend-inner-icon="mdi-lock-plus"
              :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showNewPassword = !showNewPassword"
              @input="checkPasswordStrength"
              class="mb-2"
            />

            <!-- Indicador de Força da Senha -->
            <div v-if="passwordForm.newPassword" class="mb-4">
              <div class="d-flex align-center mb-2">
                <span class="text-caption grey--text mr-2">Força da senha:</span>
                <v-chip
                  x-small
                  :color="passwordStrength.color"
                  dark
                >
                  {{ passwordStrength.label }}
                </v-chip>
              </div>

              <v-progress-linear
                :value="passwordStrength.value"
                :color="passwordStrength.color"
                height="6"
                rounded
                class="mb-2"
              />

              <div class="text-caption">
                <div
                  v-for="(req, index) in passwordRequirements"
                  :key="index"
                  class="d-flex align-center mb-1"
                >
                  <v-icon
                    x-small
                    :color="req.met ? 'success' : 'grey lighten-1'"
                    class="mr-1"
                  >
                    {{ req.met ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                  <span :class="req.met ? 'success--text' : 'grey--text'">
                    {{ req.text }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Confirmar Nova Senha -->
            <v-text-field
              v-model="passwordForm.confirmPassword"
              label="Confirmar Nova Senha *"
              outlined
              dense
              :type="showConfirmPassword ? 'text' : 'password'"
              :rules="[rules.required, validatePasswordMatch]"
              prepend-inner-icon="mdi-lock-check"
              :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showConfirmPassword = !showConfirmPassword"
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closePasswordDialog"
            :disabled="changingPassword"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="changingPassword"
            :disabled="!passwordFormValid || !isPasswordStrong"
            @click="changePassword"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar Alteração
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de Feedback -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { UsersService, FirebaseService } from '@/services'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

export default {
  name: 'UserProfile',

  data: () => ({
    valid: false,
    saving: false,

    // Dados do usuário (localStorage)
    user: {
      id: '',
      name: '',
      email: '',
      phone: '',
      avatarUrl: null,
      birthDate: null,
      hireDate: null,
      status: 'ACTIVE',
      role: '',
      jobRoleName: '',
      departmentName: '',
      managerName: '',
      tenantName: '',
      tenantAvatar: null,
    },

    // Dados do formulário (editáveis)
    formData: {
      name: '',
      email: '',
      phone: '',
      birthDate: null,
      hireDate: null,
    },

    // Dados originais para comparação
    originalData: {},

    // Preview do avatar
    previewAvatar: null,
    selectedFile: null,
    uploading: false, // Estado de upload

    // Crop de imagem
    cropDialog: false,
    imageToCrop: null,
    cropper: null,

    // Redefinição de senha
    passwordDialog: false,
    passwordFormValid: false,
    changingPassword: false,
    passwordError: '',
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
    passwordForm: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    passwordStrength: {
      value: 0,
      label: 'Fraca',
      color: 'grey',
    },

    // Regras de validação
    rules: {
      required: v => !!v || 'Campo obrigatório',
      email: v => /.+@.+\..+/.test(v) || 'Email inválido',
    },

    // Snackbar
    snackbar: {
      show: false,
      text: '',
      color: '#FF771C',
    },
  }),

  computed: {
    hasChanges() {
      return JSON.stringify(this.formData) !== JSON.stringify(this.originalData)
    },

    passwordRequirements() {
      const pwd = this.passwordForm.newPassword
      return [
        { text: 'Mínimo de 8 caracteres', met: pwd.length >= 8 },
        { text: 'Pelo menos uma letra maiúscula', met: /[A-Z]/.test(pwd) },
        { text: 'Pelo menos uma letra minúscula', met: /[a-z]/.test(pwd) },
        { text: 'Pelo menos um número', met: /[0-9]/.test(pwd) },
        { text: 'Pelo menos um caractere especial', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) },
      ]
    },

    isPasswordStrong() {
      return this.passwordRequirements.every(req => req.met)
    },
  },

  mounted() {
    this.loadUserData()
  },

  beforeDestroy() {
    // Limpar cropper ao destruir componente
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  },

  methods: {
    async loadUserData() {
      try {
        console.log('📊 Carregando perfil do usuário...')

        // Buscar dados completos do backend
        const response = await UsersService.getProfile()
        const profileData = response.data

        console.log('✅ Perfil carregado:', profileData)

        // Mapear dados do backend para o formato do componente
        this.user = {
          id: profileData.id || '',
          name: profileData.name || 'Usuário',
          email: profileData.email || '',
          phone: profileData.phone || '',
          avatarUrl: profileData.avatarUrl || null,
          birthDate: profileData.birthDate || null,
          hireDate: profileData.hireDate || null,
          status: profileData.status || 'ACTIVE',
          role: profileData.role || '-',
          jobRoleName: profileData.jobRole?.name || '-',
          departmentName: profileData.department?.name || '-',
          managerName: profileData.manager?.name || '-',
          tenantName: profileData.tenantName || '',
          tenantAvatar: profileData.tenantAvatar || null,
        }

        // Atualizar localStorage com dados frescos do backend
        localStorage.setItem('user', JSON.stringify({
          ...profileData,
          jobRoleName: this.user.jobRoleName,
          departmentName: this.user.departmentName,
          managerName: this.user.managerName,
        }))

        // Inicializar form data
        this.formData = {
          name: this.user.name,
          email: this.user.email,
          phone: this.user.phone,
          birthDate: this.user.birthDate,
          hireDate: this.user.hireDate,
        }

        // Salvar dados originais
        this.originalData = { ...this.formData }
      } catch (error) {
        console.error('❌ Erro ao carregar dados do usuário:', error)
        this.showSnackbar('Erro ao carregar dados do perfil', 'error')
      }
    },

    async updateProfile() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar('Por favor, corrija os campos inválidos', 'warning')
        return
      }

      this.saving = true

      try {
        // Preparar dados para envio (apenas campos editáveis)
        const updateData = {
          name: this.formData.name,
          email: this.formData.email,
          phone: this.formData.phone || null,
          birthDate: this.formData.birthDate || null,
        }

        // Chamar API de atualização
        const response = await UsersService.update(this.user.id, updateData)

        // Atualizar localStorage com novos dados
        const updatedUser = {
          ...this.user,
          ...response.data,
        }
        localStorage.setItem('user', JSON.stringify(updatedUser))

        // Atualizar dados locais
        this.user = { ...this.user, ...response.data }
        this.originalData = { ...this.formData }

        // Limpar estado de upload após salvar
        this.selectedFile = null
        this.previewAvatar = null
        this.avatarUrl = null

        this.showSnackbar('Perfil atualizado com sucesso!', 'success')
      } catch (error) {
        console.error('Erro ao atualizar perfil:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao atualizar perfil',
          'error'
        )
      } finally {
        this.saving = false
      }
    },

    resetForm() {
      this.formData = { ...this.originalData }
      this.previewAvatar = null
      this.selectedFile = null
      this.$refs.form.resetValidation()
    },

    // === UPLOAD DE AVATAR ===
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    async onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        this.showSnackbar('Por favor, selecione uma imagem válida', 'warning')
        return
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.showSnackbar('A imagem deve ter no máximo 5MB', 'warning')
        return
      }

      this.selectedFile = file

      // Gerar preview para o cropper
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageToCrop = e.target.result
        this.cropDialog = true

        // Inicializar cropper após o dialog abrir
        this.$nextTick(() => {
          this.initCropper()
        })
      }
      reader.readAsDataURL(file)

      // Limpar input para permitir selecionar a mesma imagem novamente
      event.target.value = ''
    },

    initCropper() {
      // Destruir cropper anterior se existir
      if (this.cropper) {
        this.cropper.destroy()
      }

      // Inicializar novo cropper
      const image = this.$refs.cropperImage
      this.cropper = new Cropper(image, {
        aspectRatio: 1, // Proporção 1:1 (quadrado)
        viewMode: 2, // Restringir área de crop dentro da imagem
        dragMode: 'move',
        autoCropArea: 0.8,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        minCropBoxWidth: 100,
        minCropBoxHeight: 100,
      })
    },

    cancelCrop() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      this.cropDialog = false
      this.imageToCrop = null
      this.selectedFile = null
    },

    async confirmCrop() {
      if (!this.cropper) return

      // Obter canvas com a imagem cropada
      const canvas = this.cropper.getCroppedCanvas({
        width: 500, // Tamanho original antes do backend fazer resize
        height: 500,
        imageSmoothingQuality: 'high',
      })

      // Converter canvas para blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          this.showSnackbar('Erro ao processar imagem', 'error')
          return
        }

        // Criar arquivo a partir do blob
        const croppedFile = new File([blob], this.selectedFile.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })

        // Gerar preview local
        this.previewAvatar = canvas.toDataURL('image/jpeg', 0.9)

        // Fechar dialog e destruir cropper
        this.cropDialog = false
        if (this.cropper) {
          this.cropper.destroy()
          this.cropper = null
        }

        // Fazer upload
        this.showSnackbar('Fazendo upload da imagem...', 'info')
        await this.uploadAvatar(croppedFile)
      }, 'image/jpeg', 0.9)
    },

    async uploadAvatar(file) {
      this.uploading = true

      try {
        console.log('📸 Fazendo upload do avatar...')

        // Chamar endpoint de upload (retorna TEXT_PLAIN com URL)
        const response = await UsersService.uploadAvatar(file)

        // Backend retorna string TEXT_PLAIN com a URL diretamente
        const avatarUrl = response.data

        console.log('✅ Avatar enviado com sucesso:', avatarUrl)

        // Atualizar URL do avatar localmente
        this.user.avatarUrl = avatarUrl

        // Atualizar no banco de dados imediatamente
        await UsersService.update(this.user.id, {
          avatarUrl: avatarUrl,
        })

        console.log('✅ Avatar salvo no banco de dados')

        // Atualizar localStorage
        const userData = JSON.parse(localStorage.getItem('user') || '{}')
        userData.avatarUrl = avatarUrl
        localStorage.setItem('user', JSON.stringify(userData))

        this.showSnackbar('Avatar atualizado com sucesso!', 'success')
      } catch (error) {
        console.error('❌ Erro ao fazer upload do avatar:', error)

        // Limpar preview em caso de erro
        this.previewAvatar = null
        this.selectedFile = null

        this.showSnackbar(
          error.response?.data?.error || 'Erro ao enviar imagem',
          'error'
        )
      } finally {
        this.uploading = false
      }
    },

    // === HELPERS ===
    getInitials(name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    getAvatarColor(name) {
      if (!name) return 'grey'
      const colors = ['#FF771C', '#F6AB2E', '#E7531A', '#7E7367', '#FF771C', '#F6AB2E']
      const charCode = name.charCodeAt(0) + name.charCodeAt(name.length - 1)
      return colors[charCode % colors.length]
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      // Adicionar 'T00:00:00' para evitar problema de timezone
      const date = new Date(dateString + 'T00:00:00')
      return date.toLocaleDateString('pt-BR')
    },

    getStatusLabel(status) {
      const labels = {
        ACTIVE: 'Ativo',
        DISABLED: 'Desativado',
        PENDING: 'Pendente',
      }
      return labels[status] || status
    },

    getRoleLabel(role) {
      const labels = {
        PLATFORM_OWNER: 'Proprietário da Plataforma',
        PARTNER_ADMIN: 'Administrador de Parceiro',
        TENANT_ADMIN: 'Administrador do Tenant',
        MANAGER: 'Gestor',
        USER: 'Usuário',
      }
      return labels[role] || role
    },

    // === REDEFINIÇÃO DE SENHA ===
    openPasswordDialog() {
      // Inicializar Firebase
      FirebaseService.init()

      this.passwordDialog = true
      this.passwordError = ''
      this.passwordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }
      this.passwordStrength = {
        value: 0,
        label: 'Fraca',
        color: 'grey',
      }

      // Resetar validação do form
      this.$nextTick(() => {
        if (this.$refs.passwordForm) {
          this.$refs.passwordForm.resetValidation()
        }
      })
    },

    closePasswordDialog() {
      this.passwordDialog = false
      this.passwordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }
      this.passwordError = ''
    },

    checkPasswordStrength() {
      const password = this.passwordForm.newPassword

      if (!password) {
        this.passwordStrength = {
          value: 0,
          label: 'Fraca',
          color: 'grey',
        }
        return
      }

      // Calcular pontuação baseada nos requisitos atendidos
      const metCount = this.passwordRequirements.filter(req => req.met).length
      const percentage = (metCount / this.passwordRequirements.length) * 100

      if (percentage < 40) {
        this.passwordStrength = {
          value: percentage,
          label: 'Fraca',
          color: 'error',
        }
      } else if (percentage < 80) {
        this.passwordStrength = {
          value: percentage,
          label: 'Média',
          color: 'warning',
        }
      } else {
        this.passwordStrength = {
          value: percentage,
          label: 'Forte',
          color: 'success',
        }
      }
    },

    validateNewPassword(value) {
      if (!value) return 'Nova senha obrigatória'

      const validation = FirebaseService.validatePasswordStrength(value)
      if (!validation.valid) {
        return validation.errors[0] // Retornar primeiro erro
      }

      return true
    },

    validatePasswordMatch(value) {
      if (!value) return 'Confirmação de senha obrigatória'
      if (value !== this.passwordForm.newPassword) {
        return 'As senhas não coincidem'
      }
      return true
    },

    async changePassword() {
      // Validar formulário
      if (!this.$refs.passwordForm.validate()) {
        return
      }

      // Verificar se a senha é forte
      if (!this.isPasswordStrong) {
        this.showSnackbar('A senha não atende aos requisitos mínimos de segurança', 'warning')
        return
      }

      this.changingPassword = true
      this.passwordError = ''

      try {
        console.log('🔒 Iniciando alteração de senha...')

        // Chamar Firebase Service para alterar senha
        await FirebaseService.changePassword(
          this.passwordForm.currentPassword,
          this.passwordForm.newPassword
        )

        console.log('✅ Senha alterada com sucesso')

        // Fechar dialog e mostrar sucesso
        this.closePasswordDialog()
        this.showSnackbar('Senha alterada com sucesso! Use a nova senha no próximo login.', 'success')

      } catch (error) {
        console.error('❌ Erro ao alterar senha:', error)

        // Mostrar erro específico no campo de senha atual
        if (error.message.includes('Senha atual incorreta')) {
          this.passwordError = error.message
        } else {
          this.showSnackbar(error.message || 'Erro ao alterar senha', 'error')
        }
      } finally {
        this.changingPassword = false
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbar = {
        show: true,
        text,
        color,
      }
    },
  },
}
</script>

<style scoped>
#user-profile {
  min-height: 100vh;
}

/* Page header */
.up-page-title {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.up-page-subtitle {
  font-size: 14px;
  margin-top: 4px;
}

/* Profile card */
.up-card-banner {
  height: 72px;
  width: 100%;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.up-profile-body {
  margin-top: -52px;
}

/* Avatar — left aligned */
.avatar-upload-container {
  position: relative;
  display: inline-block;
  margin-bottom: 0;
}

.avatar-clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
  border: 4px solid #18181b;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.55);
}

.avatar-clickable:hover {
  opacity: 0.85;
}

.avatar-upload-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.up-cyan-btn {
  background-color: #22d3ee !important;
  color: #06222b !important;
}

/* Profile card typography — left aligned */
.up-user-name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  text-align: left;
}

.up-user-role {
  font-size: 13px;
  font-weight: 500;
  margin-top: 2px;
  text-align: left;
}

.up-user-dept {
  font-size: 12px;
  margin-top: 2px;
  text-align: left;
}

/* Reset button */
.up-reset-btn {
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

/* Contact list */
.up-list-item {
  min-height: 32px !important;
}

.up-contact-text {
  font-size: 12.5px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Edit card header */
.up-edit-title {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.up-edit-subtitle {
  font-size: 13px;
  margin-top: 2px;
}

/* Crop */
.crop-container {
  width: 100%;
  max-height: 500px;
  overflow: hidden;
}
</style>

<style>
/* ════════════════════════════════════════════════════════
   DARK MODE
   Fundo da página ≈ #0d0d0d · cards um pouco mais claros (#18181b)
   · campos na cor do fundo (#0d0d0d)
   ════════════════════════════════════════════════════════ */
.theme--dark .up-profile-card.v-card,
.theme--dark .up-edit-card.v-card {
  background: #18181b !important;
  border: 1px solid #262626 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
}

.theme--dark .up-card-banner {
  background: linear-gradient(125deg, #0f2e36 0%, #102027 60%, #161618 100%);
}

.theme--dark .up-page .avatar-clickable {
  border-color: #18181b !important;
}

.theme--dark .up-page-title,
.theme--dark .up-user-name,
.theme--dark .up-edit-title {
  color: #f5f5f5;
}

.theme--dark .up-page-subtitle,
.theme--dark .up-user-role,
.theme--dark .up-user-dept,
.theme--dark .up-edit-subtitle {
  color: #888888;
}

.theme--dark .up-page .v-list-item__subtitle {
  color: #b0b0b0 !important;
}

.theme--dark .up-page .v-list.transparent {
  background: transparent !important;
}

.theme--dark .up-divider {
  border-color: #262626 !important;
}

/* Campos = cor do fundo da página */
.theme--dark .up-edit-card .v-text-field--outlined .v-input__slot {
  background-color: #0d0d0d !important;
}

.theme--dark .up-edit-card .v-text-field--outlined fieldset {
  border-color: #2a2a2a !important;
}

.theme--dark .up-edit-card .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #22d3ee !important;
}

.theme--dark .up-edit-card .v-input--is-focused .v-label {
  color: #22d3ee !important;
}

/* Ícone nativo do seletor de data (calendário) — clarear no dark */
.theme--dark .up-edit-card input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.85) brightness(1.6);
  cursor: pointer;
  opacity: 0.8;
}

/* ════════════════════════════════════════════════════════
   LIGHT MODE
   Fundo da página #F5F5F7 · cards brancos · campos = fundo
   ════════════════════════════════════════════════════════ */
.theme--light .up-profile-card.v-card,
.theme--light .up-edit-card.v-card {
  background: #FFFFFF !important;
  border: 1px solid #E5E5E7 !important;
  border-radius: 12px !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
}

.theme--light .up-card-banner {
  background: linear-gradient(125deg, #d7f4f9 0%, #eef6f8 60%, #ffffff 100%);
}

.theme--light .up-page .avatar-clickable {
  border-color: #FFFFFF !important;
}

.theme--light .up-page-title,
.theme--light .up-user-name,
.theme--light .up-edit-title {
  color: #1D1D1F !important;
}

.theme--light .up-page-subtitle,
.theme--light .up-user-role,
.theme--light .up-user-dept,
.theme--light .up-edit-subtitle {
  color: #86868B !important;
}

.theme--light .up-page .v-list-item__subtitle {
  color: #4B5563 !important;
}

.theme--light .up-page .v-list.transparent {
  background: transparent !important;
}

.theme--light .up-divider {
  border-color: #E5E5E7 !important;
}

/* Campos = cor do fundo da página */
.theme--light .up-edit-card .v-text-field--outlined .v-input__slot {
  background-color: #F5F5F7 !important;
}

.theme--light .up-edit-card .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #22d3ee !important;
}

.theme--light .up-edit-card .v-input--is-focused .v-label {
  color: #22d3ee !important;
}
</style>
